import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useAuth } from "../../contexts/AuthContext";

const API_BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

// Helper function to format wallet addresses
const formatWalletAddress = (address) => {
  if (!address || address.length < 10) return "N/A";
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

const NFTCard = ({
  nftId,
  imageUrl,
  itemName,
  priceUsd,
  creator,
  viewsCount,
  favouritesCount,
  index,
  isBlockchain = false,
  onBlockchainPurchase,
  tokenId,
  priceEth,
  // Dual-rail ("both") piece: a crypto listing that also sells by card. When
  // set, the tile surfaces the card price under the crypto price.
  cardEnabled = false,
  cardPriceUsd,
  seller,
  owner,
  ownerId,
  walletAddress,
}) => {
  const { user } = useAuth();
  // Construct the full image URL - handle both database and blockchain images
  const fullImageUrl = imageUrl
    ? imageUrl.startsWith("http")
      ? imageUrl
      : `${API_BASE_URL}/${imageUrl}`
    : "/placeholder-image.png";

  // Handle cases where nftId might be missing (optional chaining)
  const detailPageUrl = nftId ? `/marketplace/${nftId}` : "#";

  // Helper function to get initials from username
  const getInitials = (username) => {
    if (!username) return "U";
    return username.charAt(0).toUpperCase();
  };

  // Helper function to construct creator avatar URL
  const getCreatorAvatarUrl = () => {
    if (creator?.avatarUrl) {
      // Log for debugging
      console.log("Creator avatar URL:", creator.avatarUrl);
      console.log("Full avatar URL:", `${API_BASE_URL}/${creator.avatarUrl}`);
      return `${API_BASE_URL}/${creator.avatarUrl}`;
    }
    return null;
  };

  return (
    <Link href={detailPageUrl} className="block group">
      <article className="group relative h-full">
        <div className="bg-white dark:bg-jacarta-700 rounded-xl sm:rounded-2xl border border-jacarta-100 dark:border-jacarta-600 transition-shadow duration-200 hover:shadow-lg overflow-hidden h-full flex flex-col">
          {/* Image Container - Responsive aspect ratio */}
          <figure className="relative overflow-hidden bg-jacarta-50 dark:bg-jacarta-600 aspect-[4/3]">
            <Image
              src={fullImageUrl}
              alt={itemName || "Impact Art Image"}
              fill
              sizes="(max-width: 480px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
              priority={index < 4}
              className="object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </figure>

          {/* Content Section - Fluid padding */}
          <div
            className="flex-1 flex flex-col"
            style={{ padding: 'clamp(0.75rem, 2vw, 1rem)' }}
          >
            {/* Title */}
            <h3
              className="font-semibold text-jacarta-700 dark:text-white hover:text-accent transition-colors duration-200 line-clamp-2 mb-2"
              style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1rem)' }}
            >
              {itemName || "Untitled Impact Art"}
            </h3>

            {/* Creator Badge - Simplified */}
            {creator && (
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 bg-accent rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[10px] font-bold">
                    {getInitials(creator.username)}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-jacarta-500 dark:text-jacarta-400 truncate flex-1">
                  by {creator.username || "Unknown Creator"}
                </p>
              </div>
            )}

            {/* Price and Action */}
            <div className="flex items-center justify-between pt-2 border-t border-jacarta-100 dark:border-jacarta-600 mt-auto gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-jacarta-500 dark:text-jacarta-400 mb-0.5">
                  Price
                </p>
                <div className="text-xs sm:text-sm font-bold text-accent truncate">
                  {isBlockchain ? (
                    <div>
                      <div>
                        {priceEth ? parseFloat(priceEth).toFixed(3) : "0.001"}{" "}
                        ETH
                      </div>
                    </div>
                  ) : (
                    `$${priceUsd !== undefined ? Math.round(priceUsd) : "N/A"}`
                  )}
                </div>
                {/* Dual-rail piece: it also sells by card at its own USD
                    price (mirrors the detail page's "or $Y by card"). */}
                {isBlockchain && cardEnabled && cardPriceUsd != null && (
                  <div className="text-[10px] sm:text-xs font-normal text-jacarta-500 dark:text-jacarta-400 truncate">
                    or ${Math.round(cardPriceUsd)} by card
                  </div>
                )}
              </div>
              {/* Check if user owns this NFT */}
              {(() => {
                let userOwnsNFT = false;

                if (isBlockchain) {
                  // For blockchain NFTs, check wallet addresses
                  userOwnsNFT =
                    walletAddress &&
                    ((seller &&
                      typeof seller === "string" &&
                      walletAddress.toLowerCase() === seller.toLowerCase()) ||
                      (owner &&
                        typeof owner === "string" &&
                        walletAddress.toLowerCase() === owner.toLowerCase()) ||
                      (creator?.walletAddress &&
                        typeof creator.walletAddress === "string" &&
                        walletAddress.toLowerCase() ===
                          creator.walletAddress.toLowerCase()));
                } else {
                  // For offchain and card-sale, check user IDs
                  userOwnsNFT =
                    user &&
                    ((creator?.creatorId && user.id === creator.creatorId) ||
                      (owner?.ownerId && user.id === owner.ownerId) ||
                      (ownerId && user.id === ownerId));
                }

                if (userOwnsNFT) {
                  // Own pieces never get a buy affordance — send the owner to
                  // the detail page instead
                  return (
                    <button
                      className="bg-accent hover:bg-accent-dark active:scale-95 text-white font-medium py-2 px-3 rounded-lg transition-all duration-200 flex-shrink-0 min-h-[36px] sm:min-h-[40px]"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location.href = detailPageUrl;
                      }}
                    >
                      <span className="text-[10px] sm:text-xs whitespace-nowrap">
                        VIEW DETAILS
                      </span>
                    </button>
                  );
                }

                return (
                  <button
                    className="bg-accent hover:bg-accent-dark active:scale-95 text-white font-medium py-2 px-3 rounded-lg transition-all duration-200 flex-shrink-0 min-h-[36px] sm:min-h-[40px]"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // A dual-rail (crypto OR card) piece must NOT jump straight
                      // into the crypto flow — send the buyer to the detail page
                      // to pick their rail. Only a crypto-only piece buys inline.
                      if (isBlockchain && !cardEnabled && onBlockchainPurchase) {
                        onBlockchainPurchase();
                      } else {
                        window.location.href = detailPageUrl;
                      }
                    }}
                  >
                    <span className="text-[10px] sm:text-xs whitespace-nowrap">
                      {isBlockchain && cardEnabled ? "BUY" : "BUY NOW"}
                    </span>
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default NFTCard;
